import type { TenantFeatureKey } from '@mfw/common';

import { isTenantFeatureOn } from '@mfw/common';
import React from 'react';

// The two providers by module path, NOT `usePermissionsContext`:
// `PermissionsProvider.hasPermission` reads THIS hook, so going through it
// would be a provider cycle.
import { useTenantContext } from '../../context/TenantProvider';
import { useAuthContext } from '../../context/AuthProvider';

export type TenantFeaturesView = {
  /**
   * The applicable tenant is not known yet. Every answer below is "off" /
   * `false` until it is, so a gate that forgets to wait hides rather than
   * offers; a route wrapper renders nothing while it is true.
   */
  isLoading: boolean;
  isOn: (key: TenantFeatureKey) => boolean;
  canHaveAdditionalOrganizations: boolean;
  canHaveIndividualTrainers: boolean;
};

const ALL_ON: TenantFeaturesView = {
  canHaveAdditionalOrganizations: true,
  canHaveIndividualTrainers: true,
  isLoading: false,
  isOn: () => true,
};

const UNRESOLVED: TenantFeaturesView = {
  canHaveAdditionalOrganizations: false,
  canHaveIndividualTrainers: false,
  isLoading: true,
  isOn: () => false,
};

/**
 * The tenant's feature switches and organization flags as the CURRENT viewer
 * experiences them — the single frontend source for both.
 *
 * - A platform user: everything on (owner, Q1 — the same bypass as the server
 *   guard and `hasPermission`). This is why an editor of a tenant's STORED
 *   switches (the admin Features panel, the handover panel, the organization
 *   module boxes) must read the tenant row instead.
 * - Signed in: the acting tenant `/auth/me` sends (`currentUser.tenant`) — for
 *   a super admin the SELECTED tenant, which the domain may not be.
 * - Signed out: the domain tenant (`useTenantContext`).
 *
 * Sparse storage: a missing key, `{}` or NULL `features` is on. The two
 * organization flags are their own stored columns, each independent.
 *
 * Nothing is decided while the session is still being restored: a platform
 * user's `/auth/me` could still turn every answer to "on".
 */
export default function useTenantFeatures(): TenantFeaturesView {
  const { currentUser, isLoading: isAuthLoading } = useAuthContext();
  const { isLoading: isTenantLoading, tenant: domainTenant } =
    useTenantContext();

  const isPlatformUser = Boolean(currentUser?.isPlatformUser);
  const isSignedIn = Boolean(currentUser);
  const isLoading =
    !isSignedIn && (Boolean(isAuthLoading) || Boolean(isTenantLoading));
  const tenant = isSignedIn ? currentUser?.tenant : domainTenant;
  const features = tenant?.features;
  const canHaveAdditionalOrganizations = Boolean(
    tenant?.canHaveAdditionalOrganizations,
  );
  const canHaveIndividualTrainers = Boolean(tenant?.canHaveIndividualTrainers);

  return React.useMemo(() => {
    if (isPlatformUser) return ALL_ON;
    if (isLoading) return UNRESOLVED;
    return {
      canHaveAdditionalOrganizations,
      canHaveIndividualTrainers,
      isLoading: false,
      isOn: (key: TenantFeatureKey) => isTenantFeatureOn(features, key),
    };
  }, [
    canHaveAdditionalOrganizations,
    canHaveIndividualTrainers,
    features,
    isLoading,
    isPlatformUser,
  ]);
}
